<template>
    <Drawer :open="isOpen" direction="right" @update:open="(value) => !value && closeDrawer()">
        <DrawerContent class="border-l w-full sm:max-w-[90vw] sm:min-w-[320px] md:min-w-[500px] lg:min-w-[600px] max-w-4xl">
            <DrawerHeader class="flex-row items-center justify-between space-y-0 border-b px-6 py-4">
                <DrawerTitle class="text-lg font-semibold">Message Details</DrawerTitle>
                <div class="flex items-center gap-2">
                    <Button variant="outline" size="sm" @click="copyCompleteRecordAsJSON" class="gap-2">
                        <Clipboard class="h-4 w-4" />
                        {{ copyRecordDone ? 'Copied!' : 'Copy Record as JSON' }}
                    </Button>
                    <DrawerClose as-child>
                        <Button variant="ghost" size="icon" class="h-8 w-8">
                            <X class="h-4 w-4" />
                        </Button>
                    </DrawerClose>
                </div>
            </DrawerHeader>

            <div class="flex-1 overflow-y-auto px-6 py-4">
                <Tabs :tabs="tabs">
                    <template #Metadata>
                        <div class="space-y-4">
                            <div class="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                                <div class="flex flex-col">
                                    <span class="text-sm font-medium text-muted-foreground">Offset</span>
                                    <span class="font-mono text-sm">{{ message?.offset }}</span>
                                </div>
                                <div class="flex flex-col">
                                    <span class="text-sm font-medium text-muted-foreground">Partition</span>
                                    <span class="font-mono text-sm">{{ message?.partition }}</span>
                                </div>
                                <div class="flex flex-col">
                                    <span class="text-sm font-medium text-muted-foreground">Timestamp</span>
                                    <span class="font-mono text-xs">{{ message?.timestamp }}</span>
                                </div>
                                <div class="flex flex-col">
                                    <span class="text-sm font-medium text-muted-foreground">Timestamp Type</span>
                                    <span class="font-mono text-sm">{{ message?.timestamp_type }}</span>
                                </div>
                            </div>

                            <div class="p-4 bg-muted/50 rounded-lg">
                                <span class="text-sm font-medium text-muted-foreground mb-3 block">Message Sizes</span>
                                <div class="grid grid-cols-3 gap-3 text-sm">
                                    <div class="flex flex-col">
                                        <span class="text-xs text-muted-foreground">Headers</span>
                                        <span class="font-mono">{{ formatter(message?.serialized_header_size, { unit: 'bytes' }).display }}</span>
                                    </div>
                                    <div class="flex flex-col">
                                        <span class="text-xs text-muted-foreground">Key</span>
                                        <span class="font-mono">{{ formatter(message?.serialized_key_size, { unit: 'bytes' }).display }}</span>
                                    </div>
                                    <div class="flex flex-col">
                                        <span class="text-xs text-muted-foreground">Value</span>
                                        <span class="font-mono">{{ formatter(message?.serialized_value_size, { unit: 'bytes' }).display }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template #Key>
                        <div class="space-y-4">
                            <div class="flex items-center gap-2 mb-4">
                                <Button
                                    v-if="!keyExpanded && isValidJSON(message?.key)"
                                    variant="outline"
                                    size="sm"
                                    @click="
                                        keyCollapseDepth = undefined;
                                        keyExpanded = !keyExpanded;
                                    ">
                                    <Plus class="h-4 w-4 mr-1" /> Expand all
                                </Button>
                                <Button
                                    v-if="keyExpanded && isValidJSON(message?.key)"
                                    variant="outline"
                                    size="sm"
                                    @click="
                                        keyCollapseDepth = 0;
                                        keyExpanded = !keyExpanded;
                                    ">
                                    <Minus class="h-4 w-4 mr-1" /> Collapse all
                                </Button>
                                <Button variant="ghost" size="sm" @click="copyToClipboard(message?.key)">
                                    <Clipboard class="h-4 w-4 mr-1" />
                                    {{ copyDone ? 'Copied!' : 'Copy' }}
                                </Button>
                            </div>
                            <VueJsonPretty
                                v-if="isValidJSON(message?.key)"
                                :data="message?.key"
                                :show-line-number="true"
                                :show-icon="true"
                                :show-length="true"
                                collapsed-on-click-brackets="true"
                                :deep="keyCollapseDepth" />

                            <span v-else>{{ message?.key }}</span>
                        </div>
                    </template>
                    <template #Value>
                        <div class="space-y-4">
                            <div class="flex items-center gap-2 mb-4">
                                <Button
                                    v-if="!valueExpanded && isValidJSON(message?.value)"
                                    variant="outline"
                                    size="sm"
                                    @click="
                                        valueCollapseDepth = undefined;
                                        valueExpanded = !valueExpanded;
                                    ">
                                    <Plus class="h-4 w-4 mr-1" /> Expand all
                                </Button>
                                <Button
                                    v-if="valueExpanded && isValidJSON(message?.value)"
                                    variant="outline"
                                    size="sm"
                                    @click="
                                        valueCollapseDepth = 0;
                                        valueExpanded = !valueExpanded;
                                    ">
                                    <Minus class="h-4 w-4 mr-1" /> Collapse all
                                </Button>
                                <Button variant="ghost" size="sm" @click="copyToClipboard(message?.value)">
                                    <Clipboard class="h-4 w-4 mr-1" />
                                    {{ copyDone ? 'Copied!' : 'Copy' }}
                                </Button>
                            </div>
                            <VueJsonPretty
                                v-if="isValidJSON(message?.value)"
                                :data="message?.value"
                                :show-line-number="true"
                                :show-icon="true"
                                :show-length="true"
                                collapsed-on-click-brackets="true"
                                :deep="valueCollapseDepth" />

                            <span v-else>{{ message?.value }}</span>
                        </div>
                    </template>

                    <template #value_schema>
                        <div class="space-y-4">
                            <div class="flex items-center gap-2 mb-4">
                                <Button
                                    v-if="!valueSchemaExpanded && isValidJSON(message?.value_schema)"
                                    variant="outline"
                                    size="sm"
                                    @click="
                                        valueSchemaCollapseDepth = undefined;
                                        valueSchemaExpanded = !valueSchemaExpanded;
                                    ">
                                    <Plus class="h-4 w-4 mr-1" /> Expand all
                                </Button>
                                <Button
                                    v-if="valueSchemaExpanded && isValidJSON(message?.value_schema)"
                                    variant="outline"
                                    size="sm"
                                    @click="
                                        valueSchemaCollapseDepth = 0;
                                        valueSchemaExpanded = !valueSchemaExpanded;
                                    ">
                                    <Minus class="h-4 w-4 mr-1" /> Collapse all
                                </Button>
                                <Button variant="ghost" size="sm" @click="copyToClipboard(message?.value_schema)">
                                    <Clipboard class="h-4 w-4 mr-1" />
                                    {{ copyDone ? 'Copied!' : 'Copy' }}
                                </Button>
                            </div>
                            <VueJsonPretty
                                v-if="isValidJSON(message?.value_schema)"
                                :data="message?.value_schema"
                                :show-line-number="true"
                                :show-icon="true"
                                :show-length="true"
                                collapsed-on-click-brackets="true"
                                :deep="valueSchemaCollapseDepth" />

                            <span v-else>{{ message?.value_schema }}</span>
                        </div>
                    </template>

                    <template #key_schema>
                        <div class="space-y-4">
                            <div class="flex items-center gap-2 mb-4">
                                <Button
                                    v-if="!keySchemaExpanded && isValidJSON(message?.key_schema)"
                                    variant="outline"
                                    size="sm"
                                    @click="
                                        keySchemaCollapseDepth = undefined;
                                        keySchemaExpanded = !keySchemaExpanded;
                                    ">
                                    <Plus class="h-4 w-4 mr-1" /> Expand all
                                </Button>
                                <Button
                                    v-if="keySchemaExpanded && isValidJSON(message?.key_schema)"
                                    variant="outline"
                                    size="sm"
                                    @click="
                                        keySchemaCollapseDepth = 0;
                                        keySchemaExpanded = !keySchemaExpanded;
                                    ">
                                    <Minus class="h-4 w-4 mr-1" /> Collapse all
                                </Button>
                                <Button variant="ghost" size="sm" @click="copyToClipboard(message?.key_schema)">
                                    <Clipboard class="h-4 w-4 mr-1" />
                                    {{ copyDone ? 'Copied!' : 'Copy' }}
                                </Button>
                            </div>
                            <VueJsonPretty
                                v-if="isValidJSON(message?.key_schema)"
                                :data="message?.key_schema"
                                :show-line-number="true"
                                :show-icon="true"
                                :show-length="true"
                                collapsed-on-click-brackets="true"
                                :deep="keySchemaCollapseDepth" />

                            <span v-else>{{ message?.key_schema }}</span>
                        </div>
                    </template>

                    <template #Headers>
                        <div class="space-y-4">
                            <div class="flex items-center gap-2 mb-4">
                                <Button
                                    v-if="!headersExpanded && isValidJSON(message?.headers)"
                                    variant="outline"
                                    size="sm"
                                    @click="
                                        headersCollapseDepth = undefined;
                                        headersExpanded = !headersExpanded;
                                    ">
                                    <Plus class="h-4 w-4 mr-1" /> Expand all
                                </Button>
                                <Button
                                    v-if="headersExpanded && isValidJSON(message?.headers)"
                                    variant="outline"
                                    size="sm"
                                    @click="
                                        headersCollapseDepth = 0;
                                        headersExpanded = !headersExpanded;
                                    ">
                                    <Minus class="h-4 w-4 mr-1" /> Collapse all
                                </Button>
                                <Button variant="ghost" size="sm" @click="copyToClipboard(message?.headers)">
                                    <Clipboard class="h-4 w-4 mr-1" />
                                    {{ copyDone ? 'Copied!' : 'Copy' }}
                                </Button>
                            </div>
                            <VueJsonPretty
                                v-if="isValidJSON(message?.headers)"
                                :data="message?.headers"
                                :show-line-number="true"
                                :show-icon="true"
                                :show-length="true"
                                collapsed-on-click-brackets="true"
                                :deep="headersCollapseDepth" />

                            <span v-else>{{ message?.headers }}</span>
                        </div>
                    </template>
                </Tabs>
            </div>
        </DrawerContent>
    </Drawer>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import { Clipboard, Plus, Minus, X } from 'lucide-vue-next';
import VueJsonPretty from 'vue-json-pretty';
import _ from 'lodash';
import Tabs from '@/components/global/Tabs.vue';
import { formatter } from '@/utils/utils';
import { Drawer, DrawerClose, DrawerContent, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';

const props = defineProps({
    message: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['close']);

// Separate collapse depth state for each tab to prevent contamination
const keyCollapseDepth = ref(1);
const valueCollapseDepth = ref(1);
const valueSchemaCollapseDepth = ref(1);
const keySchemaCollapseDepth = ref(1);
const headersCollapseDepth = ref(1);

const keyExpanded = ref(false);
const valueExpanded = ref(false);
const headerExpanded = ref(false);
const valueSchemaExpanded = ref(false);
const keySchemaExpanded = ref(false);

const isOpen = ref(false);
const tabs = ['Metadata', 'Key', 'Value', { name: 'Value Schema', key: 'value_schema' }, { name: 'Key Schema', key: 'key_schema' }, 'Headers'];
const copyDone = ref(false);
const copyRecordDone = ref(false);

const drawerWidth = ref(1200); // Default drawer width in pixels
const MIN_WIDTH = 800; // Minimum width in pixels
const MAX_WIDTH = 1600; // Maximum width in pixels
const isResizing = ref(false);

const isValidJSON = (json) => {
    try {
        _.cloneDeep(json);
        return true;
    } catch (e) {
        return false;
    }
};

watch(
    () => props.message,
    () => {
        // Reset expanded states
        keyExpanded.value = false;
        valueExpanded.value = false;
        headerExpanded.value = false;
        valueSchemaExpanded.value = false;
        keySchemaExpanded.value = false;

        // Reset collapse depths to default state
        keyCollapseDepth.value = 1;
        valueCollapseDepth.value = 1;
        valueSchemaCollapseDepth.value = 1;
        keySchemaCollapseDepth.value = 1;
        headersCollapseDepth.value = 1;
    }
);

function copyToClipboard(textToCopy) {
    copyDone.value = true;
    navigator.clipboard
        .writeText(textToCopy && typeof textToCopy === 'object' ? JSON.stringify(textToCopy, null, 2) : textToCopy)
        .then(() => setTimeout(() => (copyDone.value = false), 1000))
        .catch((err) => {
            console.error('Could not copy text: ', err);
        });
}

/**
 * Copy complete record as JSON - includes all fields for unit testing
 * Format: { key, value, keySchema, valueSchema, headers, metadata }
 */
function copyCompleteRecordAsJSON() {
    if (!props.message) return;

    copyRecordDone.value = true;

    // Create comprehensive record object for unit testing
    const completeRecord = {
        // Core message data
        key: props.message.key,
        value: props.message.value,
        keySchema: props.message.key_schema,
        valueSchema: props.message.value_schema,
        headers: props.message.headers,

        // Metadata for testing context
        metadata: {
            offset: props.message.offset,
            partition: props.message.partition,
            timestamp: props.message.timestamp,
            timestampType: props.message.timestamp_type,
            serializedHeaderSize: props.message.serialized_header_size,
            serializedKeySize: props.message.serialized_key_size,
            serializedValueSize: props.message.serialized_value_size
        }
    };

    const jsonString = JSON.stringify(completeRecord, null, 2);

    navigator.clipboard
        .writeText(jsonString)
        .then(() => {
            setTimeout(() => (copyRecordDone.value = false), 2000);
        })
        .catch((err) => {
            console.error('Could not copy complete record: ', err);
            copyRecordDone.value = false;
        });
}

const closeDrawer = () => {
    isOpen.value = false;
    emit('close');
};

watch(
    () => props.message,
    (newVal) => {
        isOpen.value = !!newVal;
    }
);

onMounted(() => {
    isOpen.value = !!props.message;
});

function startResizing() {
    isResizing.value = true;

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';

    document.addEventListener('mousemove', resizeDrawer);
    document.addEventListener('mouseup', stopResizing);
}

function resizeDrawer(e) {
    if (!isResizing.value) return;

    let newWidth = window.innerWidth - e.clientX;

    newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newWidth));

    drawerWidth.value = newWidth;

    e.preventDefault();
}

function stopResizing() {
    isResizing.value = false;
    document.removeEventListener('mousemove', resizeDrawer);
    document.removeEventListener('mouseup', stopResizing);

    document.body.style.userSelect = '';
    document.body.style.cursor = '';
}
</script>

<style scoped>
@reference '../../style.css';

/* Custom resize handle styling */
.group\/drawer-content[data-vaul-drawer-direction='right'] {
    will-change: transform;
}

/* Ensure smooth resizing */
.group\/drawer-content {
    transition: none !important;
}

/* Ensure content wraps properly during resize */
.group\/drawer-content * {
    transition: none !important;
}

/* Better resize handle visibility */
.group\/drawer-content:hover .resize-handle {
    background-color: hsl(var(--primary) / 0.3);
}

/* Fix VueJsonPretty hover styles with proper Vue 3 + Tailwind theming */
:deep(.vjs-tree) .vjs-tree-node {
    padding: 2px 4px;
    margin: 1px 0;
    border-radius: 4px;
    transition: all 0.15s ease;
}

:deep(.vjs-tree) .vjs-tree-node:hover {
    background-color: hsl(var(--accent)) !important;
    box-shadow: var(--shadow-xs);
}

:deep(.vjs-tree) .vjs-tree-node:hover .vjs-key,
:deep(.vjs-tree) .vjs-tree-node:hover .vjs-value,
:deep(.vjs-tree) .vjs-tree-node:hover .vjs-value-string,
:deep(.vjs-tree) .vjs-tree-node:hover .vjs-value-number,
:deep(.vjs-tree) .vjs-tree-node:hover .vjs-value-boolean,
:deep(.vjs-tree) .vjs-tree-node:hover .vjs-value-null,
:deep(.vjs-tree) .vjs-tree-node:hover .vjs-tree-brackets,
:deep(.vjs-tree) .vjs-tree-node:hover .vjs-colon,
:deep(.vjs-tree) .vjs-tree-node:hover .vjs-comment {
    color: hsl(var(--accent-foreground)) !important;
}

/* Keep line numbers subtly different on hover */
:deep(.vjs-tree) .vjs-tree-node:hover .vjs-node-index {
    color: hsl(var(--muted-foreground)) !important;
    font-weight: 500;
}
</style>
